/**
 * ─── HRIS Leave Management + 13th Month + Reports ─────────────
 * Cloned from PayrollProSolutions: leave credits, DTR import,
 * 13th month pay, tardiness/overtime reports, deduction ledger.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { employeeRepo, payrollRepo } from '../../lib/db'
import { EMPLOYMENT_STATUS_LABELS } from '@repo/entity-hris'
import {
  Calendar, Plus, FileText, Clock, AlertTriangle, TrendingUp,
  DollarSign, Download, Upload, Users,
} from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// 1. Leave Management
// ═══════════════════════════════════════════════════════════════

interface LeaveRecord {
  id: string; employeeId: string; employeeName: string
  leaveType: string; leaveDate: string; daysUsed: number; notes: string; status: string
}

export function HrisLeavePage() {
  const [leaves, setLeaves] = useState<LeaveRecord[]>([])
  const [employees, setEmployees] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ employeeId: '', leaveType: 'VL', leaveDate: '', daysUsed: 0.5, notes: '' })

  useEffect(() => {
    const stored = localStorage.getItem('hris_leaves')
    if (stored) setLeaves(JSON.parse(stored))
    ;(async () => {
      try { const r = await employeeRepo.findMany({ page: 1, pageSize: 500 }); setEmployees('items' in r ? r.items : []) }
      catch { }
    })()
  }, [])

  function save() {
    const emp = employees.find((e: any) => e.id === form.employeeId)
    const entry: LeaveRecord = {
      ...form, id: Date.now().toString(), employeeId: form.employeeId,
      employeeName: emp?.fullName ?? emp?.name ?? form.employeeId,
      leaveDate: form.leaveDate || new Date().toISOString().slice(0, 10), status: 'approved',
    }
    const updated = [entry, ...leaves]
    setLeaves(updated)
    localStorage.setItem('hris_leaves', JSON.stringify(updated))
    setShowModal(false)
    setForm({ employeeId: '', leaveType: 'VL', leaveDate: '', daysUsed: 0.5, notes: '' })
  }

  const leaveTypes = ['VL', 'SL', 'EL', 'PL', 'ML', 'BL', 'UL']

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Leave Management</h2><p className="text-sm text-gray-500 mt-1">{leaves.length} leave records</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Record Leave</Button>
      </div>

      {/* Employee Leave Summary */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Leave Credits Summary" description="Per employee leave balance" />
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['Employee','VL Used','SL Used','Total Days'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {employees.slice(0, 10).map((e: any) => {
                const empLeaves = leaves.filter(l => l.employeeId === e.id)
                const vl = empLeaves.filter(l => l.leaveType === 'VL').reduce((s, l) => s + l.daysUsed, 0)
                const sl = empLeaves.filter(l => l.leaveType === 'SL').reduce((s, l) => s + l.daysUsed, 0)
                return (<tr key={e.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-medium">{e.fullName ?? e.name}</td><td className="px-3 py-2.5 text-center">{vl}</td><td className="px-3 py-2.5 text-center">{sl}</td><td className="px-3 py-2.5 text-center font-bold">{empLeaves.reduce((s,l) => s + l.daysUsed, 0)}</td></tr>)
              })}
              {employees.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-gray-400">No employees. Add employees first.</td></tr>}
            </tbody>
          </table></div>
        </Card>

        <Card>
          <CardHeader title="Recent Leave Records" description={`${leaves.length} entries`} />
          <div className="overflow-x-auto max-h-96 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['Date','Employee','Type','Days','Status'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {leaves.slice(0, 15).map(l => (<tr key={l.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs">{l.leaveDate}</td><td className="px-3 py-2.5 font-medium">{l.employeeName}</td><td className="px-3 py-2.5"><Badge color={l.leaveType==='VL'?'blue':l.leaveType==='SL'?'yellow':'green'}>{l.leaveType}</Badge></td><td className="px-3 py-2.5 text-center">{l.daysUsed}</td><td className="px-3 py-2.5"><Badge color="green">{l.status}</Badge></td></tr>))}
              {leaves.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-gray-400">No leave records.</td></tr>}
            </tbody>
          </table></div>
        </Card>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Record Leave" size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} icon={<Calendar className="h-4 w-4"/>}>Save</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Employee</label><select value={form.employeeId} onChange={e => setForm({...form, employeeId: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">Select...</option>{employees.map((e:any)=><option key={e.id} value={e.id}>{e.fullName ?? e.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label><select value={form.leaveType} onChange={e => setForm({...form, leaveType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{leaveTypes.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <Input label="Days" type="number" value={String(form.daysUsed)} onChange={e => setForm({...form, daysUsed: Number(e.target.value)})} />
          <Input label="Date" type="date" value={form.leaveDate} onChange={e => setForm({...form, leaveDate: e.target.value})} />
          <div className="sm:col-span-2"><Input label="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 2. 13th Month Pay Computation
// ═══════════════════════════════════════════════════════════════

export function Hris13thMonthPage() {
  const [employees, setEmployees] = useState<any[]>([])
  const [payrolls, setPayrolls] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [e, p] = await Promise.all([
        employeeRepo.findMany({ page: 1, pageSize: 500 }),
        payrollRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      setEmployees('items' in e ? e.items : [])
      setPayrolls('items' in p ? p.items : [])
    } catch { } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">13th Month Pay</h2><p className="text-sm text-gray-500 mt-1">Computed as: (Total Basic Salary Earned) ÷ 12 months</p></div>
      <Card><CardHeader title="13th Month Computation" description={`${employees.length} employees`} />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Employee','Monthly Salary','Total Basic Earned','13th Month','Tax-Exempt','Taxable'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={6} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             employees.map((emp: any) => {
               const monthly = emp.basicSalary ?? emp.baseSalary ?? 0
               const totalBasic = monthly * 12
               const thirteenth = Math.round(totalBasic / 12 * 100) / 100
               const exempt = Math.min(thirteenth, 90000)
               const taxable = Math.max(0, thirteenth - exempt)
               return (
              <tr key={emp.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-medium">{emp.fullName ?? emp.name}</td>
                <td className="px-3 py-2.5">₱{monthly.toLocaleString()}</td>
                <td className="px-3 py-2.5">₱{totalBasic.toLocaleString()}</td>
                <td className="px-3 py-2.5 font-bold text-green-700">₱{thirteenth.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-green-600">₱{exempt.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-red-600">{taxable > 0 ? `₱${taxable.toLocaleString()}` : '—'}</td>
              </tr>
             )})}
          </tbody>
        </table></div>
      </Card>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 3. Reports Dashboard (Tardiness, Overtime, Deduction Ledger)
// ═══════════════════════════════════════════════════════════════

export function HrisReportsPage() {
  const [employees, setEmployees] = useState<any[]>([])
  const [payrolls, setPayrolls] = useState<any[]>([])
  const [attendance, setAttendance] = useState<any[]>([])
  const [leaves, setLeaves] = useState<any[]>([])

  useEffect(() => {
    ;(async () => {
      try {
        const [e, p] = await Promise.all([
          employeeRepo.findMany({ page: 1, pageSize: 500 }),
          payrollRepo.findMany({ page: 1, pageSize: 500 }),
        ])
        setEmployees('items' in e ? e.items : [])
        setPayrolls('items' in p ? p.items : [])
      } catch { }
      try { setAttendance(JSON.parse(localStorage.getItem('hris_attendance_records') || '[]')) } catch { }
      try { setLeaves(JSON.parse(localStorage.getItem('hris_leaves') || '[]')) } catch { }
    })()
  }, [])

  const totalGross = payrolls.reduce((s: number, p: any) => s + (p.grossPay ?? 0), 0)
  const totalNet = payrolls.reduce((s: number, p: any) => s + (p.netPay ?? 0), 0)
  const totalLate = attendance.filter((a: any) => a.status === 'late').length
  const totalAbsent = attendance.filter((a: any) => a.status === 'absent').length
  const totalOT = attendance.reduce((s: number, a: any) => s + (a.overtimeHours ?? 0), 0)
  const totalLeaves = leaves.reduce((s: number, l: any) => s + (l.daysUsed ?? 0), 0)

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">Payroll Reports</h2><p className="text-sm text-gray-500 mt-1">Summary reports per cutoff period — cloned from PayrollProSolutions</p></div>

      {/* Summary Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Total Gross', `₱${totalGross.toLocaleString()}`, 'green', DollarSign],
          ['Total Net', `₱${totalNet.toLocaleString()}`, 'blue', TrendingUp],
          ['Overtime Hours', `${totalOT.toFixed(1)}h`, 'amber', Clock],
          ['Late Incidents', totalLate, 'yellow', AlertTriangle],
          ['Absences', totalAbsent, 'red', AlertTriangle],
          ['Leave Days', totalLeaves, 'purple', Calendar],
          ['Employees', employees.length, 'indigo', Users],
          ['Payroll Runs', payrolls.length, 'teal', FileText],
        ].map(([label, value, color, Icon]: any) => (
          <div key={label} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <div className="flex items-center gap-2">{Icon && <Icon className={`h-4 w-4 text-${color}-600`} />}<p className={`text-xs text-${color}-600`}>{label}</p></div>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tardiness Summary */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
        <Card>
          <CardHeader title="Tardiness Summary" description={`${totalLate} late incidents`} />
          <div className="overflow-x-auto max-h-80 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['Date','Employee','Status','Hours'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {attendance.filter((a: any) => a.status === 'late').slice(0, 15).map((a: any) => (<tr key={a.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs">{a.date}</td><td className="px-3 py-2.5 font-medium">{a.employeeName}</td><td className="px-3 py-2.5"><Badge color="yellow">Late</Badge></td><td className="px-3 py-2.5">{a.hoursWorked?.toFixed(1)}h</td></tr>))}
              {attendance.filter((a: any) => a.status === 'late').length === 0 && <tr><td colSpan={4} className="py-8 text-center text-gray-400">No tardiness records.</td></tr>}
            </tbody>
          </table></div>
        </Card>

        {/* Overtime Summary */}
        <Card>
          <CardHeader title="Overtime Summary" description={`${totalOT.toFixed(1)} total OT hours`} />
          <div className="overflow-x-auto max-h-80 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['Date','Employee','OT Hours'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {attendance.filter((a: any) => a.overtimeHours > 0).slice(0, 15).map((a: any) => (<tr key={a.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 text-xs">{a.date}</td><td className="px-3 py-2.5 font-medium">{a.employeeName}</td><td className="px-3 py-2.5 text-center font-bold text-blue-600">{a.overtimeHours?.toFixed(1)}h</td></tr>))}
              {attendance.filter((a: any) => a.overtimeHours > 0).length === 0 && <tr><td colSpan={3} className="py-8 text-center text-gray-400">No overtime records.</td></tr>}
            </tbody>
          </table></div>
        </Card>
      </div>

      {/* Employee Deduction Ledger */}
      <Card>
        <CardHeader title="Employee Deduction Ledger" description="All deductions per employee — SSS, PhilHealth, Pag-IBIG, Withholding Tax" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Employee','SSS','PhilHealth','Pag-IBIG','Withholding','Total Deductions','Net Pay','Period'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {payrolls.slice(0, 20).map((p: any) => (<tr key={p.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-medium text-xs">{p.employeeName ?? p.employeeId?.slice(0,8)}</td><td className="px-3 py-2.5 text-right">₱{(p.sssContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right">₱{(p.philhealthContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right">₱{(p.pagibigContribution??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-orange-600">₱{(p.withholdingTax??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right font-bold text-red-600">₱{(p.totalDeductions??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right font-bold text-green-700">₱{(p.netPay??0).toLocaleString()}</td><td className="px-3 py-2.5 text-xs text-gray-400">{p.periodStart ? new Date(p.periodStart).toLocaleDateString() : '—'}</td></tr>))}
            {payrolls.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No payroll runs yet.</td></tr>}
          </tbody>
        </table></div>
      </Card>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 4. DTR / Biometric Time Log Import
// ═══════════════════════════════════════════════════════════════

export function HrisDTRPage() {
  const [logs, setLogs] = useState<any[]>([])
  const [message, setMessage] = useState('')
  const [parsed, setParsed] = useState<any[]>([])

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const lines = text.split('\n').filter(l => l.trim())
      // Parse CSV: User ID, Name, Date, Time, M Code
      const records: any[] = []
      for (const line of lines.slice(1)) { // Skip header
        const parts = line.split(',').map(p => p.trim())
        if (parts.length >= 4) {
          records.push({
            userId: parts[0], name: parts[1], date: parts[2], time: parts[3],
            mCode: parts[4] || '00',
          })
        }
      }
      setParsed(records)
      // Save to localStorage
      const existing = JSON.parse(localStorage.getItem('hris_dtr_logs') || '[]')
      const updated = [...records, ...existing].slice(0, 5000)
      localStorage.setItem('hris_dtr_logs', JSON.stringify(updated))
      setLogs(updated.slice(0, 50))
      setMessage(`Imported ${records.length} DTR records from ${file.name}`)
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    try { setLogs(JSON.parse(localStorage.getItem('hris_dtr_logs') || '[]').slice(0, 50)) } catch { }
  }, [])

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">DTR / Biometric Import</h2><p className="text-sm text-gray-500 mt-1">Import time logs from biometric devices (CSV format: User ID, Name, Date, Time, M Code)</p></div>

      <div className="mb-6 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-8 text-center">
        <Upload className="h-10 w-10 mx-auto mb-4 text-blue-500" />
        <p className="text-sm font-medium text-blue-700 mb-2">Upload DTR CSV File</p>
        <p className="text-xs text-blue-500 mb-4">Format: User ID, Name, Date, Time, M Code</p>
        <input type="file" accept=".csv,.txt" onChange={handleFileUpload}
          className="block mx-auto text-sm text-blue-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200" />
      </div>

      {message && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{message}</div>}

      {parsed.length > 0 && (
        <Card>
          <CardHeader title="Imported Records" description={`${parsed.length} time log entries`} />
          <div className="overflow-x-auto max-h-96 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['User ID','Name','Date','Time','M Code'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {parsed.map((r, i) => (<tr key={i} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-mono text-xs">{r.userId}</td><td className="px-3 py-2.5">{r.name}</td><td className="px-3 py-2.5">{r.date}</td><td className="px-3 py-2.5 font-mono">{r.time}</td><td className="px-3 py-2.5">{r.mCode}</td></tr>))}
            </tbody>
          </table></div>
        </Card>
      )}

      {logs.length > 0 && parsed.length === 0 && (
        <Card>
          <CardHeader title="Previously Imported" description={`${logs.length} records`} />
          <div className="overflow-x-auto max-h-96 overflow-y-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>{['User ID','Name','Date','Time'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {logs.map((r: any, i: number) => (<tr key={i} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-mono text-xs">{r.userId}</td><td className="px-3 py-2.5">{r.name}</td><td className="px-3 py-2.5">{r.date}</td><td className="px-3 py-2.5 font-mono">{r.time}</td></tr>))}
            </tbody>
          </table></div>
        </Card>
      )}
    </div>
  )
}
